import { type CSSProperties, type KeyboardEvent, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ProductTemplate } from "@cyh/contracts";
import { useLanguage } from "../../i18n";
import { fuzzyProductTemplates } from "./product-template-search";

export function ProductTemplateCombobox({ products, valueId, onChange }: { products: ProductTemplate[]; valueId: string; onChange: (id: string) => void }) {
  const { t, language } = useLanguage();
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const emittedValueId = useRef(valueId);
  const selected = products.find((product) => product.id === valueId) ?? null;
  const selectedLabel = selected ? productTemplateLabel(selected, language) : "";
  const [query, setQuery] = useState(selectedLabel);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const options = useMemo(() => fuzzyProductTemplates(products, query === selectedLabel ? "" : query, 20), [products, query, selectedLabel]);

  useEffect(() => {
    if (emittedValueId.current === valueId) return;
    emittedValueId.current = valueId;
    setQuery(selectedLabel);
  }, [valueId, selectedLabel]);
  useEffect(() => { setActiveIndex(0); }, [query]);
  useLayoutEffect(() => {
    if (!open) return;
    function positionMenu() {
      const rectangle = rootRef.current?.getBoundingClientRect();
      if (!rectangle) return;
      const margin = 12;
      const gap = 5;
      const width = Math.min(560, Math.max(rectangle.width, 300), window.innerWidth - margin * 2);
      const left = Math.max(margin, Math.min(rectangle.left, window.innerWidth - margin - width));
      const spaceBelow = window.innerHeight - rectangle.bottom - gap - margin;
      const spaceAbove = rectangle.top - gap - margin;
      const opensAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
      setMenuStyle({ left, width, maxHeight: Math.min(310, Math.max(100, opensAbove ? spaceAbove : spaceBelow)), top: opensAbove ? "auto" : rectangle.bottom + gap, bottom: opensAbove ? window.innerHeight - rectangle.top + gap : "auto" });
    }
    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => { window.removeEventListener("resize", positionMenu); window.removeEventListener("scroll", positionMenu, true); };
  }, [open]);

  function select(product: ProductTemplate) {
    emittedValueId.current = product.id;
    onChange(product.id);
    setQuery(productTemplateLabel(product, language));
    setOpen(false);
  }
  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); setActiveIndex((index) => Math.min(index + 1, Math.max(0, options.length - 1))); }
    else if (event.key === "ArrowUp") { event.preventDefault(); setActiveIndex((index) => Math.max(0, index - 1)); }
    else if (event.key === "Enter" && open && options[activeIndex]) { event.preventDefault(); select(options[activeIndex]!); }
    else if (event.key === "Escape") setOpen(false);
  }

  return <div ref={rootRef} className="model-combobox product-template-combobox" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <input className="model-combobox-input" disabled={!products.length} autoComplete="off" role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls={listId} value={query} onFocus={(event) => { setOpen(true); if (selected) event.currentTarget.select(); }} onChange={(event) => { emittedValueId.current = ""; setQuery(event.target.value); onChange(""); setOpen(true); }} onKeyDown={handleKeyDown} placeholder={products.length ? t("输入产品编码或中英文名称", "Type product code or name") : t("暂无产品组合", "No product set")} />
    {open && createPortal(<div className="model-combobox-menu" id={listId} role="listbox" style={menuStyle}>
      {options.length ? options.map((product, index) => <button type="button" role="option" aria-selected={valueId === product.id} className={index === activeIndex ? "active" : ""} key={product.id} onMouseEnter={() => setActiveIndex(index)} onMouseDown={(event) => { event.preventDefault(); select(product); }}><strong>{product.sku} · {language === "en" ? product.nameEn || product.name : product.name}</strong>{product.nameEn && language !== "en" && <small>{product.nameEn}</small>}</button>) : <div className="model-combobox-state">{t("没有匹配的产品", "No matching products")}</div>}
    </div>, document.body)}
  </div>;
}

function productTemplateLabel(product: ProductTemplate, language: "zh-CN" | "en"): string {
  return `${product.sku} · ${language === "en" ? product.nameEn || product.name : product.name}`;
}
